'use client';

import React from 'react';
import { motion } from 'framer-motion';
import AppImage from '@/components/ui/AppImage';

interface HeroSectionProps {
  title: string;
  subtitle: string;
  description: string;
}

const HeroSection: React.FC<HeroSectionProps> = ({ title, subtitle, description }) => {
  return (
    <section className="relative bg-gradient-to-br from-primary/10 via-secondary/5 to-accent/10 py-10 sm:py-16 lg:py-24">
      <div className="container mx-auto px-4">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="space-y-6 text-center lg:text-left"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="inline-block px-4 py-2 bg-primary/10 rounded-full"
            >
              <p className="text-primary font-semibold text-sm">{subtitle}</p>
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="text-4xl lg:text-5xl xl:text-6xl font-heading font-bold text-foreground leading-tight"
            >
              {title}
            </motion.h1>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="text-lg text-muted-foreground leading-relaxed"
            >
              {description}
            </motion.p>
            <motion.div
              initial="hidden"
              animate="visible"
              variants={{
                hidden: { opacity: 0 },
                visible: {
                  opacity: 1,
                  transition: {
                    staggerChildren: 0.1,
                    delayChildren: 0.5,
                  },
                },
              }}
              className="flex justify-center flex-wrap gap-4 pt-4"
            >
              <motion.div
                variants={{
                  hidden: { opacity: 0, y: 20 },
                  visible: { opacity: 1, y: 0 },
                }}
                whileHover={{ scale: 1.05 }}
                className="flex items-center space-x-3 bg-card px-6 py-4 rounded-lg shadow-subtle"
              >
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                  <span className="text-2xl font-bold text-primary">4.9</span>
                </div>
                <div>
                  <p className="font-semibold text-foreground">Rating</p>
                  <p className="text-sm text-muted-foreground">535+ Reviews</p>
                </div>
              </motion.div>
              <motion.div
                variants={{
                  hidden: { opacity: 0, y: 20 },
                  visible: { opacity: 1, y: 0 },
                }}
                whileHover={{ scale: 1.05 }}
                className="flex items-center space-x-3 bg-card px-6 py-4 rounded-lg shadow-subtle"
              >
                <div className="w-12 h-12 bg-secondary/10 rounded-full flex items-center justify-center">
                  <span className="text-2xl font-bold text-secondary">10K+</span>
                </div>
                <div>
                  <p className="font-semibold text-foreground">Lives Touched</p>
                  <p className="text-sm text-muted-foreground">Since 2020</p>
                </div>
              </motion.div>
            </motion.div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="relative"
          >
            <motion.div
              whileHover={{ scale: 1.02 }}
              transition={{ duration: 0.3 }}
              className="relative rounded-2xl overflow-hidden shadow-elevated"
            >
              <AppImage
                src="https://img.rocket.new/generatedImages/rocket_gen_img_13f010258-1764669848385.png"
                alt="Diverse group of volunteers in blue Seva Setu Trust t-shirts distributing food packages to elderly community members in outdoor setting"
                className="w-full h-[220px] sm:h-[320px] lg:h-[420px] object-cover" />

              <div className="absolute inset-0 bg-gradient-to-t from-foreground/60 to-transparent" />
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.8 }}
                className="absolute bottom-6 left-6 right-6 text-white"
              >
                <p className="text-xl font-display font-semibold">सेवा ए परमो धर्म</p>
                <p className="text-sm opacity-90">Service is the Supreme Duty</p>
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>);

};

export default HeroSection;